import { http } from '../../../../api/http';
import type {
  CTSCalculoResult,
  DepositoCTS,
  GratificacionCalculoResult,
  ProvisionMensualResult,
  ProvisionMensual,
  Vacacion,
  VacacionGoce,
  VacacionAlertaVencimiento,
  VacacionResumenTrabajador,
  RegistrarGoceRequest,
  Prestamo,
  PrestamoCuota,
  ResumenBeneficios,
  GrabarCTSRequest,
} from '../types/beneficios';

const ENDPOINT = '/planilla/beneficios';

export class BeneficiosService {
  // ── CTS ─────────────────────────────────────────────────────

  static async calcularCTS(semestre: number, anio: number): Promise<CTSCalculoResult[]> {
    const response = await http.get(`${ENDPOINT}/cts/calcular`, { params: { semestre, anio } });
    return response.data;
  }

  static async calcularCTSTrabajador(idTrabajador: number, semestre: number, anio: number): Promise<CTSCalculoResult> {
    const response = await http.get(`${ENDPOINT}/cts/calcular/${idTrabajador}`, { params: { semestre, anio } });
    return response.data;
  }

  static async grabarCTS(request: GrabarCTSRequest): Promise<DepositoCTS[]> {
    const response = await http.post(`${ENDPOINT}/cts/grabar`, request);
    return response.data;
  }

  static async getDepositosCTS(anio?: number, idTrabajador?: number): Promise<DepositoCTS[]> {
    const response = await http.get(`${ENDPOINT}/cts/depositos`, { params: { anio, idTrabajador } });
    return response.data;
  }

  // ── Gratificaciones ─────────────────────────────────────────

  static async calcularGratificacion(semestre: number, anio: number): Promise<GratificacionCalculoResult[]> {
    const response = await http.get(`${ENDPOINT}/gratificacion/calcular`, { params: { semestre, anio } });
    return response.data;
  }

  static async calcularGratificacionTrabajador(idTrabajador: number, semestre: number, anio: number): Promise<GratificacionCalculoResult> {
    const response = await http.get(`${ENDPOINT}/gratificacion/calcular/${idTrabajador}`, { params: { semestre, anio } });
    return response.data;
  }

  // ── Provisiones ─────────────────────────────────────────────

  static async calcularProvisiones(idPeriodo: number): Promise<ProvisionMensualResult[]> {
    const response = await http.get(`${ENDPOINT}/provisiones/calcular/${idPeriodo}`);
    return response.data;
  }

  static async grabarProvisiones(idPeriodo: number): Promise<ProvisionMensual[]> {
    const response = await http.post(`${ENDPOINT}/provisiones/grabar/${idPeriodo}`);
    return response.data;
  }

  static async getProvisiones(idPeriodo?: number, idTrabajador?: number): Promise<ProvisionMensual[]> {
    const response = await http.get(`${ENDPOINT}/provisiones`, { params: { idPeriodo, idTrabajador } });
    return response.data;
  }

  // ── Vacaciones ──────────────────────────────────────────────

  static async getVacaciones(idTrabajador: number): Promise<Vacacion[]> {
    const response = await http.get(`${ENDPOINT}/vacaciones/${idTrabajador}`);
    return response.data;
  }

  static async registrarVacacion(vacacion: Vacacion): Promise<Vacacion> {
    const response = await http.post(`${ENDPOINT}/vacaciones`, vacacion);
    return response.data;
  }

  static async actualizarVacacion(vacacion: Vacacion): Promise<Vacacion> {
    const response = await http.put(`${ENDPOINT}/vacaciones`, vacacion);
    return response.data;
  }

  // ── Vacaciones Mejoradas ──────────────────────────────────────

  static async generarPeriodos(idTrabajador: number): Promise<Vacacion[]> {
    const response = await http.post(`${ENDPOINT}/vacaciones/${idTrabajador}/generar-periodos`);
    return response.data;
  }

  static async getVacacionConGoces(idVacacion: number): Promise<Vacacion> {
    const response = await http.get(`${ENDPOINT}/vacaciones/periodo/${idVacacion}`);
    return response.data;
  }

  static async registrarGoce(request: RegistrarGoceRequest): Promise<VacacionGoce> {
    const response = await http.post(`${ENDPOINT}/vacaciones/goce`, request);
    return response.data;
  }

  static async eliminarGoce(idVacacionGoce: number): Promise<void> {
    await http.delete(`${ENDPOINT}/vacaciones/goce/${idVacacionGoce}`);
  }

  static async getVacacionesPorVencer(diasUmbral: number = 90): Promise<VacacionAlertaVencimiento[]> {
    const response = await http.get(`${ENDPOINT}/vacaciones/por-vencer`, { params: { diasUmbral } });
    return response.data;
  }

  static async getResumenTodos(): Promise<VacacionResumenTrabajador[]> {
    const response = await http.get(`${ENDPOINT}/vacaciones/resumen-todos`);
    return response.data;
  }

  static async getCuentaCorriente(idTrabajador: number): Promise<VacacionResumenTrabajador> {
    const response = await http.get(`${ENDPOINT}/vacaciones/cuenta-corriente/${idTrabajador}`);
    return response.data;
  }

  // ── Préstamos ───────────────────────────────────────────────

  static async getPrestamos(idTrabajador?: number, estado?: string): Promise<Prestamo[]> {
    const response = await http.get(`${ENDPOINT}/prestamos`, { params: { idTrabajador, estado } });
    return response.data;
  }

  static async crearPrestamo(prestamo: Prestamo): Promise<Prestamo> {
    const response = await http.post(`${ENDPOINT}/prestamos`, prestamo);
    return response.data;
  }

  static async anularPrestamo(idPrestamo: number): Promise<Prestamo> {
    const response = await http.put(`${ENDPOINT}/prestamos/${idPrestamo}/anular`);
    return response.data;
  }

  static async getCuotas(idPrestamo: number): Promise<PrestamoCuota[]> {
    const response = await http.get(`${ENDPOINT}/prestamos/${idPrestamo}/cuotas`);
    return response.data;
  }

  static async updateCuota(idCuota: number, monto: number, fechaProgramada: string): Promise<PrestamoCuota> {
    const response = await http.put(`${ENDPOINT}/prestamos/cuotas/${idCuota}`, { monto, fechaProgramada });
    return response.data;
  }

  static async reprogramarCuotas(idPrestamo: number, cuotasAdicionales: number): Promise<Prestamo> {
    const response = await http.put(`${ENDPOINT}/prestamos/${idPrestamo}/reprogramar`, { cuotasAdicionales });
    return response.data;
  }

  // ── Resumen ─────────────────────────────────────────────────

  static async getResumen(idTrabajador: number): Promise<ResumenBeneficios> {
    const response = await http.get(`${ENDPOINT}/resumen/${idTrabajador}`);
    return response.data;
  }
}
